import { useMutation } from '@tanstack/react-query'
import { IamNav} from '@weirgroup/weir-dls'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import settingsApi from '../../api/settings.api'
import { AppConfigContext } from '../../contexts/app-config.context'
import { supportedLanguages } from '../../helpers/constants'
import { UserSettingType } from '../../types/user.type'
import TimezoneModal from '../timezone-modal/timezone.modal'
import UnitsModal from '../units-modal/units.modal'

import './styles.scss'
import { ConfirmDialog } from '../confirm-dialog/confirm-dialog'
import { deleteAccount } from '../../api/users.api'
import { logout } from '../../utils/logout.util'
import { ExternalFleetApiModal } from '../external-fleet-api-modal/external-fleet-api-modal'
import { fetchExternalFleetApi } from '../../api/external-fleet.api'
import { useDarkMode } from '../../hooks/use-darkmode'
import { useNavigate } from 'react-router-dom'
import { routePath } from '../../constants/navigation.constants'
import { LOGOUT, MY_ACCOUNT } from '../../constants/profile-menu-click'
import { YOUR_WEIR_HUB, YOUR_WEIR_PRODUCTION_MY_ACCOUNT, YOUR_WEIR_STAGING_MY_ACCOUNT } from '../../constants/links.constants'
import { APP_NAME } from '../../constants/app-name'
import { useAnalyticsEvent } from '../../hooks/useAnalyticsEvent'

type Props = { isAuthenticated: boolean }

export const Navigation = (props: Props = {
    isAuthenticated: false
}) => {
    const logAnalyticsEvent = useAnalyticsEvent();
    const navigate = useNavigate();
    const { isAuthenticated } = props
    const { t, i18n } = useTranslation()
    const [unitsModalOpen, setUnitsModalOpen] = useState(false)
    const [timezoneModalOpen, setTimezoneModalOpen] = useState(false)
    const appConfigStore = useContext(AppConfigContext)
    const [language, setLanguage] = useState(appConfigStore.language)
    const { mutate, isPending: isLoading } = useMutation({mutationFn:settingsApi.updateUserSettings})
    const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [isExternalFleetModalShown, setIsExternalFleetModalShown] = useState<boolean>(false)
    const [externalFleetApi, setExternalFleetApi] = useState<any>()
    const {darkMode} = useDarkMode()
    useEffect(() => {
        setLanguage(appConfigStore.language)
        i18n.changeLanguage(appConfigStore.language)
    }, [appConfigStore.language])

    useEffect(() => {
        getExternalFleetApi();
    }, [])

    const toggleUnitsDialog = () => {
        setUnitsModalOpen(!unitsModalOpen)
    }
    const toggleTimezoneDialog = () => {
        setTimezoneModalOpen(!timezoneModalOpen)
    }
    const deleteUserAccount = () => {
        setShowLoader(true);
        deleteAccount().then(() => {
            setShowLoader(false)
            logout(true);
        }).catch((err: any) => {
            console.error(err);
            setShowLoader(false);
            setIsConfirmOpen(false);
        })
    }

   
    const profileMenuItems = [
        {
            id: 1,
            menuitem: t('externalFleetApi'),
            onSelect: () => setIsExternalFleetModalShown(!isExternalFleetModalShown),
        },
        {
            id: 2,
            menuitem: t('units'),
            onSelect: toggleUnitsDialog,
        },
        {
            id: 3,
            menuitem: t('timezone'),
            onSelect: toggleTimezoneDialog,
        },
        {
            id: 4,
            menuitem: t('deleteAccount'),
            onSelect: () => setIsConfirmOpen(true),
        },
    ]

    const onLanguageChange = async (selectedLanguage: string) => {
        setLanguage(selectedLanguage)
        appConfigStore.setLanguage(selectedLanguage)
        const userSettings: UserSettingType = {
            language: selectedLanguage,
            timezone: appConfigStore.timezone,
            units: appConfigStore.units,
            asset_email_notification: appConfigStore.assetEmailNotification,
            geofence_email_notification: appConfigStore.geofenceEmailNotification,
            inapp_notification: appConfigStore.inAppNotification
        }
        await mutate(userSettings)
        i18n.changeLanguage(selectedLanguage)
    }

    const onProfileMenuClick = (selected: string) => {

        if(selected === MY_ACCOUNT){
            window.location.assign(
                    window.location.hostname.toLowerCase() === YOUR_WEIR_HUB
                        ? YOUR_WEIR_STAGING_MY_ACCOUNT
                        : YOUR_WEIR_PRODUCTION_MY_ACCOUNT
            );
        }
        if(selected === LOGOUT){
            //  Add the msal logout and remove the local storage
            logout()
            logAnalyticsEvent('user_logout', { description: 'User logged out of the application' });
        }
        const menuItem = profileMenuItems.find(
            (item) => item.menuitem === selected
        )
        menuItem?.onSelect()
    }

    const getExternalFleetApi = () => {
        fetchExternalFleetApi().then((result: any) => {
            setExternalFleetApi(result.data.data)
        })
    }

    return (
        <div className="navbar-container" data-testid="navbar">
            {isConfirmOpen ? <ConfirmDialog
                isConfirmOpen={isConfirmOpen}
                confirmHandler={() => deleteUserAccount()}
                cancelHandler={() => setIsConfirmOpen(false)}
                showLoader={showLoader}
                confirmMessage={t("deleteConfirmMessage")}
            /> : <></>}
            <div>
                <UnitsModal
                    open={unitsModalOpen}
                    toggleDialog={toggleUnitsDialog}
                />
                <TimezoneModal
                    open={timezoneModalOpen}
                    toggleDialog={toggleTimezoneDialog}
                />
                <ExternalFleetApiModal
                    open={isExternalFleetModalShown}
                    toggleDialog={() => setIsExternalFleetModalShown(!isExternalFleetModalShown)}
                    externalFleetApi={externalFleetApi}
                />
                <div className={isAuthenticated ? 'iamNavAuth' : 'iamNavUnAuth'}>
                    <IamNav
                        darkMode={darkMode}
                        appId={process.env.REACT_APP_MSAL_CLIENT_ID ?? ''}
                        inAppNav
                        redirectUri={
                            process.env.REACT_APP_MSAL_REDIRECT_URI ?? ''
                        }
                        langSwitcher={!isLoading}
                        profileMenuItems={profileMenuItems}
                        defaultLanguageCode={language.toLowerCase() == 'EN' ? 'en-us' : language}
                        supportedLanguages={supportedLanguages}
                        onLangChange={onLanguageChange}
                        onProfileMenuItemClick={onProfileMenuClick}
                        contactTitle={t('contactForm.contactTitle')}
                        contactAppname={t('contactForm.contactAppname')}
                        subjectRequired={t('contactForm.subjectRequired')}
                        subjectLabel={t('contactForm.subjectLabel')}
                        subjectPlaceholder={t('contactForm.subjectPlaceholder')}
                        messageRequired={t('contactForm.messageRequired')}
                        messageLabel={t('contactForm.messageLabel')}
                        messagePlaceholder={t('contactForm.messagePlaceholder')}
                        buttonLabelClose={t('contactForm.buttonLabelClose')}
                        buttonLabelSubmit={t('contactForm.buttonLabelSubmit')}
                        contactDescription={t('contactForm.contactDescription')}
                        notificationTitleSuccess={t(
                            'contactForm.notificationTitleSuccess'
                        )}
                        notificationTitleError={t(
                            'contactForm.notificationTitleError'
                        )}
                        notificationTextContent={t(
                            'contactForm.notificationTextContent'
                        )}
                        baseUrl='https://apis.apps.weir/iamservice'
                        headerVersion='2'
                        userToken={localStorage.getItem('accessToken')} // use accessToken
                        appMenuhomeLink={APP_NAME}
                        onLogoClick={() => {
                            navigate(routePath.HOMEPAGE)
                        }}
                        // userInstance={instance} // Use our MSAL Instance : msal_service.ts Pass the instance (Required for logout)
                    />
                </div>
            </div>
        </div>
    )
}
